'use client'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { ImageWithPlaceholder } from '@/components/ImageWithPlaceholder/ImageWithPlaceholder'
import { type SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { getImgSrc } from '@/services/storyblok/Storyblok.helpers'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { StoryblokComponent, storyblokEditable } from '@storyblok/react'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Button } from 'ui/src/components/Button/Button'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { ArrowDownIcon } from 'ui/src/icons/ArrowIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { visuallyHidden } from 'ui/src/utils/visibility.css'
import { SignUpDialog } from '../SignUpDialog/SignUpDialog'
import { VINTAGE_SHOP_PRODUCT_CONTENT_TYPE } from '../vintageShop.constants'
import { type VintageShopProductStory } from '../vintageShop.types'
import { VintageShopFooter } from '../VintageShopFooter/VintageShopFooter'
import {
  additionalImagesSection,
  additionalImageWrapper,
  backButton,
  badge,
  contentBodyColumn,
  contentSection,
  handwrittenPriceImage,
  header,
  headerPrice,
  heroSection,
  introSection,
  mainLayout,
  priceCard,
  priceCards,
  priceGraphImage,
  productHeading,
  root,
} from './VintageShopProductBlock.css'

type Props = SbBaseBlockProps<VintageShopProductStory['content']>

export function VintageShopProductBlock(props: Props) {
  const { blok } = props
  const { t } = useTranslation(['vintageShop', 'common'])
  const locale = useRoutingLocale()

  const [isSignUpOpen, setIsSignUpOpen] = useState(false)

  const isSold = blok.isSold === true
  const isComingSoon = !isSold && !blok.stripePaymentUrl?.url
  const isAvailable = !isSold && !!blok.stripePaymentUrl?.url

  return (
    <>
      <article className={root} {...storyblokEditable(blok)}>
        <GridLayout.Root>
          <GridLayout.Content width={{ _: '1' }} align="center">
            <div className={mainLayout}>
              <section className={heroSection}>
                <Button
                  as="a"
                  href={PageLink.vintageShop({ locale }).pathname}
                  variant="ghost"
                  size="icon"
                  Icon={<ArrowDownIcon style={{ transform: 'rotate(90deg)' }} />}
                  className={backButton}
                  aria-label={t('VINTAGE_SHOP_BACK_BUTTON_LABEL')}
                />
                <ImageWithPlaceholder
                  alt={blok.featuredImage.alt ?? ''}
                  className={sprinkles({ objectFit: 'cover' })}
                  src={getImgSrc(blok.featuredImage.filename)}
                  fill={true}
                  imagePriority={true}
                  sizes="(min-width: 1024px) 50vw, 100vw"
                />
              </section>

              <div className={contentBodyColumn}>
                <div className={contentSection}>
                  <header className={header}>
                    <Heading
                      as="h1"
                      balance={true}
                      className={productHeading}
                      variant={{ _: 'standard.18', lg: 'standard.24' }}
                    >
                      {blok.pageHeading}
                    </Heading>
                    <div className={yStack({ gap: 'none', justifyContent: 'flex-end', alignItems: 'flex-end' })}>
                      {blok.currentPrice && (
                        <Text as="span" size={{ _: 'md', lg: 'xl' }} strikethrough={true} className={headerPrice}>
                          {blok.currentPrice}
                        </Text>
                      )}
                      {blok.ourPrice &&
                        (blok.priceImage?.filename ? (
                          <>
                            <ImageWithPlaceholder
                              src={getImgSrc(blok.priceImage.filename)}
                              alt=""
                              aria-hidden={true}
                              width={0}
                              height={0}
                              sizes="200px"
                              className={handwrittenPriceImage}
                            />
                            <span className={visuallyHidden}>{blok.ourPrice}</span>
                          </>
                        ) : (
                          <Text as="span" size={{ _: 'md', lg: 'xl' }} className={headerPrice}>
                            {blok.ourPrice}
                          </Text>
                        ))}
                    </div>
                  </header>

                  <div className={xStack({ gap: 'xs', marginBottom: 'xl' })}>
                    {isSold && (
                      <Badge size="large" variant="yellow3">
                        {t('VINTAGE_SHOP_SOLD_LABEL')}
                      </Badge>
                    )}

                    {isComingSoon && (
                      <>
                        <Button variant="primary-alt" onClick={() => setIsSignUpOpen(true)}>
                          {t('VINTAGE_SHOP_SIGN_UP_BUTTON')}
                        </Button>
                        <Badge size="large" variant="grey1" className={badge}>
                          {t('VINTAGE_SHOP_COMING_SOON_LABEL')}
                        </Badge>
                      </>
                    )}

                    {isAvailable && (
                      <Button
                        as="a"
                        href={blok.stripePaymentUrl?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="primary"
                      >
                        {t('VINTAGE_SHOP_BUY_BUTTON')}
                      </Button>
                    )}
                  </div>

                  {blok.intro?.length ? (
                    <div className={introSection}>
                      {blok.intro.map((item) => (
                        <StoryblokComponent blok={item} key={item._uid} nested={true} />
                      ))}
                    </div>
                  ) : null}

                  <div className={priceCards}>
                    {blok.originalPrice && (
                      <div className={priceCard}>
                        <Text as="span" color="textSecondary" size={{ _: 'xs', lg: 'md' }}>
                          {t('VINTAGE_SHOP_ORIGINAL_PRICE_LABEL')}
                        </Text>
                        <Text as="span" size={{ _: 'md', lg: 'xl' }}>
                          {blok.originalPrice}
                        </Text>
                      </div>
                    )}
                    {blok.currentPrice && (
                      <div className={priceCard}>
                        <Text as="span" color="textSecondary" size={{ _: 'xs', lg: 'md' }}>
                          {t('VINTAGE_SHOP_CURRENT_VALUE_LABEL')}
                        </Text>
                        <Text as="span" size={{ _: 'md', lg: 'xl' }}>
                          {blok.currentPrice}
                        </Text>
                      </div>
                    )}
                    {blok.ourPrice && (
                      <div className={priceCard}>
                        <Text as="span" color="textSecondary" size={{ _: 'xs', lg: 'md' }}>
                          {t('VINTAGE_SHOP_OUR_PRICE_LABEL')}
                        </Text>
                        <Text as="span" size={{ _: 'md', lg: 'xl' }}>
                          {blok.ourPrice}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>

                <div className={yStack({ gap: { _: 'xxl' } })}>
                  {blok.priceIncrease && (
                    <div className={yStack({ gap: 'md' })}>
                      <div className={xStack({ gap: 'xs' })}>
                        <Text size="md">{t('VINTAGE_SHOP_PRICE_INCREASE_HEADING')}</Text>
                        <Text size="md" color="signalGreenElement">
                          {blok.priceIncrease}
                        </Text>
                      </div>
                      <div className={yStack({ gap: 'xs' })}>
                        {blok.priceGraphImage && (
                          <ImageWithPlaceholder
                            src={getImgSrc(blok.priceGraphImage.filename)}
                            alt={blok.priceGraphImage.alt ?? ''}
                            width={0}
                            height={0}
                            sizes="100vw"
                            className={priceGraphImage}
                          />
                        )}
                        <Text size="md" color="textSecondary">
                          {t('VINTAGE_SHOP_PRICE_INCREASE_HEADING_NOTE')}
                        </Text>
                      </div>
                    </div>
                  )}

                  <div>
                    {blok.body.map((item) => (
                      <StoryblokComponent blok={item} key={item._uid} nested={true} />
                    ))}
                  </div>
                </div>
              </div>

              <div className={additionalImagesSection}>
                {blok.additionalImages?.length ? (
                  <div className={yStack({ gap: { _: 'xs', lg: 'md' } })}>
                    {blok.additionalImages.map((img) => (
                      <div key={img.filename} className={additionalImageWrapper}>
                        <ImageWithPlaceholder
                          src={getImgSrc(img.filename)}
                          alt={img.alt ?? ''}
                          fill
                          className={sprinkles({ objectFit: 'cover' })}
                          sizes="(min-width: 1024px) 50vw, 100vw"
                        />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </GridLayout.Content>
        </GridLayout.Root>

        <VintageShopFooter readMoreDialog={blok.readMoreDialog} showAllProductsLink={true} />
      </article>
      <SignUpDialog open={isSignUpOpen} onOpenChange={setIsSignUpOpen} />
    </>
  )
}

VintageShopProductBlock.blockName = VINTAGE_SHOP_PRODUCT_CONTENT_TYPE
